import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorText } from "@/lib/api";
import { txt } from "@/lib/format";
import { DIETS, LABELS } from "@/lib/icons";
import { BilingualFields } from "./Bilingual";
import { ImageUpload } from "./ImageUpload";
import { LangSwitch } from "@/components/LangSwitch";
import { useAdminLang } from "./context";

const numStr = (v) => (v === null || v === undefined ? "" : String(v));
const toForm = (item, defaultCat) => ({
  category_id: item?.category_id || defaultCat || "",
  title_en: item?.title_en || "", title_ar: item?.title_ar || "",
  description_en: item?.description_en || "", description_ar: item?.description_ar || "",
  price: numStr(item?.price), original_price: numStr(item?.original_price), promotional_price: numStr(item?.promotional_price),
  image_url: item?.image_url || null, labels: item?.labels || [], dietary_tags: item?.dietary_tags || [],
  is_visible: item?.is_visible ?? true, is_available: item?.is_available ?? true,
});

function validate(f) {
  const e = {};
  const num = (v) => v !== "" && Number.isFinite(Number(v)) && Number(v) >= 0;
  if (!f.category_id) e.category_id = "val.category";
  if (!f.title_en.trim() && !f.title_ar.trim()) e.title = "val.title";
  if (!num(f.price)) e.price = "val.price";
  if (f.original_price !== "" && !num(f.original_price)) e.original_price = "val.number";
  if (f.promotional_price !== "" && !num(f.promotional_price)) e.promotional_price = "val.number";
  return e;
}

const Chips = ({ options, value, onChange, t, prefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const on = value.includes(o);
      return (
        <button key={o} type="button" aria-pressed={on} data-testid={`chip-${prefix}-${o}`} onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
          className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-primary bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
          {t(`${prefix}.${o}`)}
        </button>
      );
    })}
  </div>
);

const PriceInput = ({ id, label, value, onChange, error, t }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} inputMode="decimal" dir="ltr" value={value} onChange={(e) => onChange(e.target.value)} data-testid={id} className={error ? "border-destructive" : ""} />
    {error && <p className="text-xs text-red-400" data-testid={`${id}-error`}>{t(error)}</p>}
  </div>
);

export default function ItemEditor({ item, categories, defaultCategory, onClose, onSaved, t, lang, currency }) {
  const [form, setForm] = useState(() => toForm(item, defaultCategory));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { setLang } = useAdminLang();
  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    setErrors((e) => (Object.keys(e).length ? validate(next) : e));
    return next;
  });

  const save = async () => {
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) return toast.error(t("val.fix"));
    const opt = (v) => (v === "" ? null : Number(v));
    const body = { ...form, price: Number(form.price), original_price: opt(form.original_price), promotional_price: opt(form.promotional_price) };
    setSaving(true);
    try {
      const r = item?.id ? await api.put(`/admin/items/${item.id}`, body) : await api.post("/admin/items", body);
      toast.success(t("toast.saved"));
      onSaved(r.data);
    } catch (err) {
      toast.error(errorText(err, t("toast.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="item-editor">
        <DialogHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0 pe-8">
          <DialogTitle>{item?.id ? t("editor.editTitle") : t("editor.newTitle")}</DialogTitle>
          <LangSwitch lang={lang} onChange={setLang} testid="editor-lang-switch" />
        </DialogHeader>
        <div className="grid gap-6 md:grid-cols-[260px_1fr]">
          <div className="space-y-2">
            <Label>{t("editor.photo")}</Label>
            <ImageUpload value={form.image_url} onChange={(v) => set("image_url", v)} t={t} testid="item-image-upload" />
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("editor.category")}</Label>
              <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
                <SelectTrigger data-testid="item-category-select" className={errors.category_id ? "border-destructive" : ""}><SelectValue placeholder={t("editor.chooseCategory")} /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`item-category-option-${c.id}`}>{c.menu_name_en || c.menu_name_ar ? `${txt({ name_en: c.menu_name_en, name_ar: c.menu_name_ar }, "name", lang)} › ` : ""}{txt(c, "name", lang)}</SelectItem>)}</SelectContent>
              </Select>
              {errors.category_id && <p className="text-xs text-red-400">{t(errors.category_id)}</p>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <PriceInput id="item-price-input" label={`${t("editor.price")} (${currency})`} value={form.price} onChange={(v) => set("price", v)} error={errors.price} t={t} />
              <PriceInput id="item-original-price-input" label={t("editor.originalPrice")} value={form.original_price} onChange={(v) => set("original_price", v)} error={errors.original_price} t={t} />
              <PriceInput id="item-promo-price-input" label={t("editor.promoPrice")} value={form.promotional_price} onChange={(v) => set("promotional_price", v)} error={errors.promotional_price} t={t} />
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_visible} onCheckedChange={(v) => set("is_visible", v)} data-testid="item-visible-switch" />{t("editor.visible")}</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_available} onCheckedChange={(v) => set("is_available", v)} data-testid="item-available-switch" />{t("editor.available")}</label>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label>{t("editor.content")}</Label>
          {errors.title && <p className="text-xs text-red-400" data-testid="item-title-error">{t(errors.title)}</p>}
          <BilingualFields form={form} set={set} t={t} testPrefix="item" errors={errors} fields={[{ key: "title" }, { key: "description", multiline: true }]} />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2"><Label>{t("editor.labels")}</Label><Chips options={LABELS} value={form.labels} onChange={(v) => set("labels", v)} t={t} prefix="label" /></div>
          <div className="space-y-2"><Label>{t("editor.dietary")}</Label><Chips options={DIETS} value={form.dietary_tags} onChange={(v) => set("dietary_tags", v)} t={t} prefix="diet" /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="item-cancel-btn">{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="item-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin" />}{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
